/**
 * <Text> — the base typography atom.
 *
 * Every piece of text in a DS-built screen goes through this atom (or, preferably,
 * through the `<Title>` / `<Paragraph>` encapsulations that pin the common presets).
 * Styling is driven by Unistyles: a `StyleSheet.create` dynamic-function style reads
 * the font size, weight (family) and color from the runtime `theme` tokens. When the
 * persona themes start to diverge, only the token values change — the prop contract
 * and this component stay identical.
 *
 * No hex literals: color comes from a `SemanticColorKey` token, never a raw string.
 */
import React from "react"
import { Text as RNText, type StyleProp, type TextProps as RNTextProps, type TextStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../../theme/tokens/colors"
import { type FontFamilyKey, type FontSizeKey } from "../../../theme/tokens/typography"

export type TextProps = {
    children: React.ReactNode
    /** Font size token — defaults to `text-base` (le libellé de bouton). */
    size?: FontSizeKey
    /** Outfit weight token — defaults to `regular`. */
    weight?: FontFamilyKey
    /** Semantic color token — defaults to `text-primary`. */
    color?: SemanticColorKey
    uppercase?: boolean
    center?: boolean
    numberOfLines?: RNTextProps["numberOfLines"]
    style?: StyleProp<TextStyle>
    testID?: string
} & Pick<RNTextProps, "onPress" | "ellipsizeMode" | "selectable" | "accessibilityRole">

export function Text(props: TextProps) {
    const {
        children,
        size = "text-base",
        weight = "regular",
        color = "text-primary",
        uppercase = false,
        center = false,
        numberOfLines = 0,
        style,
        testID,
        ...rest
    } = props

    return (
        <RNText
            testID={testID}
            numberOfLines={numberOfLines}
            style={[styles.text(size, weight, color, uppercase, center), style]}
            {...rest}
        >
            {children}
        </RNText>
    )
}

const styles = StyleSheet.create((theme) => ({
    text: (size: FontSizeKey, weight: FontFamilyKey, color: SemanticColorKey, uppercase: boolean, center: boolean) => ({
        fontSize: theme.fontSizes[size],
        fontFamily: theme.fontFamilies[weight],
        color: theme.colors[color],
        textTransform: uppercase ? "uppercase" : "none",
        textAlign: center ? "center" : "left",
    }),
}))
